import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  DwActionPreference,
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwFormActionConfig,
  DwFormActionInterceptResult,
  DwFormActionInterceptor,
  DwFormActionKeys,
  DwMetaDataFormApi,
  DwMetaDataFormSectionStateService,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwSectionReadonlyBaseComponent,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken,
  DwUiConfigRegistryService
} from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  AppMetaData,
  AppMetaDataItemNames,
  FlightSchoolExaminerEntity,
  UnitMemberQualificationOptionEntity,
  UnitMemberQualificationResponseEntity,
  UnitMemberQualificationTypeEntity
} from '../../../../meta-data/app-meta-data.service';
import {
  UnitMemberQualificationData,
  UnitMemberQualificationRepositoryService
} from '../../services/unit-member-qualification-repository.service';

interface UnitMemberQualificationQuestion {
  definition: UnitMemberQualificationTypeEntity;
  options: UnitMemberQualificationOptionEntity[];
  response: UnitMemberQualificationResponseEntity;
  itemNumber?: number;
}

@DwComponent({
  key: 'unit-member-qualification-questionnaire',
  name: 'Unit Member Qualification Questionnaire',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.FlightSchoolExaminer
})
@Component({
  selector: 'app-unit-member-qualification-questionnaire',
  templateUrl: './unit-member-qualification-questionnaire.component.html',
  styleUrls: ['./unit-member-qualification-questionnaire.component.scss']
})
export class UnitMemberQualificationQuestionnaireComponent extends DwSectionReadonlyBaseComponent implements OnInit, OnDestroy {
  readonly responseTypes = {
    yesNo: 1,
    number: 2,
    singleChoice: 3
  };

  singleChoiceQuestions: UnitMemberQualificationQuestion[] = [];
  tableQuestions: UnitMemberQualificationQuestion[] = [];
  loading = true;
  dirty = false;
  readOnly = true;
  message: string;
  errorMessage: string;

  private formApi: DwMetaDataFormApi;
  private flightSchoolExaminerId: number;
  private formActionInterceptor: DwFormActionInterceptor;

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwMetaDataFormSectionStateService: DwMetaDataFormSectionStateService,
    dwMetaDataFormStateService: DwMetaDataFormStateService,
    private qualificationRepository: UnitMemberQualificationRepositoryService
  ) {
    super(
      dwExpressionService,
      dwMetaDataFormSectionStateService,
      dwMetaDataFormStateService,
      dwUiConfigRegistryService,
      uiMetaDataConfig,
      dwMetaDataService
    );
    this.formApi = dwMetaDataFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.sectionReadOnlyChanged(this.isSectionReadOnly());
    this.formActionInterceptor = this.getFormActionInterceptor();
    this.dwFormStateService.addActionInterceptor(this.formActionInterceptor);

    this.subscriptions.push(
      this.formApi.getFormDataChanges<FlightSchoolExaminerEntity>(100)
        .subscribe(examiner => this.loadForExaminer(examiner))
    );

    if (this.formApi.isDataLoadComplete) {
      this.loadForExaminer(this.formApi.getFormData<FlightSchoolExaminerEntity>());
    }
  }

  ngOnDestroy(): void {
    if (this.formActionInterceptor) {
      this.dwFormStateService.removeActionInterceptor(this.formActionInterceptor);
    }
    super.ngOnDestroy();
  }

  sectionReadOnlyChanged(readOnly: boolean): void {
    this.readOnly = readOnly;
  }

  setYesNo(question: UnitMemberQualificationQuestion, value: boolean): void {
    if (this.readOnly) {
      return;
    }

    question.response.YesNoValue = value;
    question.response.NumericValue = null;
    question.response.UnitMemberQualificationOptionId = null;
    this.markDirty();
  }

  setNumeric(question: UnitMemberQualificationQuestion, value: number): void {
    if (this.readOnly) {
      return;
    }

    question.response.YesNoValue = null;
    question.response.NumericValue = value === null || value === undefined ? null : Number(value);
    question.response.UnitMemberQualificationOptionId = null;
    this.markDirty();
  }

  setOption(question: UnitMemberQualificationQuestion, optionId: number): void {
    if (this.readOnly) {
      return;
    }

    question.response.YesNoValue = null;
    question.response.NumericValue = null;
    question.response.UnitMemberQualificationOptionId = optionId;
    this.markDirty();
  }

  clearAnswer(question: UnitMemberQualificationQuestion): void {
    if (this.readOnly) {
      return;
    }

    question.response.YesNoValue = null;
    question.response.NumericValue = null;
    question.response.UnitMemberQualificationOptionId = null;
    this.markDirty();
  }

  getValidationMessage(question: UnitMemberQualificationQuestion): string {
    const value = question.response.NumericValue;
    const maximum = question.definition.MaximumValue;

    if (value !== null && value !== undefined && value < 0) {
      return 'Enter zero or a positive number.';
    }
    if (value !== null && value !== undefined && maximum !== null && maximum !== undefined && value > maximum) {
      return `Maximum allowed value is ${maximum}.`;
    }
    return '';
  }

  private saveResponses(): Observable<DwFormActionInterceptResult> {
    const result: DwFormActionInterceptResult = { abort: false };
    const questions = this.singleChoiceQuestions.concat(this.tableQuestions);
    const validationError = questions.find(question => !!this.getValidationMessage(question));
    if (validationError) {
      result.abort = true;
      result.message = 'Correct the highlighted qualification response before saving.';
      return of(result);
    }

    const responses = questions
      .map(question => question.response)
      .filter(response => !!response.UnitMemberQualificationResponseId || this.hasAnswer(response));

    if (responses.length === 0) {
      this.dirty = false;
      return of(result);
    }

    this.message = null;
    this.errorMessage = null;

    return this.qualificationRepository.save(responses).pipe(
      map(savedResponses => {
        this.applySavedResponses(savedResponses || []);
        this.dirty = false;
        return result;
      }),
      catchError(() => {
        result.abort = true;
        result.message = 'Qualification responses could not be saved.';
        return of(result);
      })
    );
  }

  trackQuestion(index: number, question: UnitMemberQualificationQuestion): number {
    return question.definition.UnitMemberQualificationTypeId;
  }

  trackOption(index: number, option: UnitMemberQualificationOptionEntity): number {
    return option.UnitMemberQualificationOptionId;
  }

  private loadForExaminer(examiner: FlightSchoolExaminerEntity): void {
    if (!this.formApi.isDataLoadComplete) {
      return;
    }

    const examinerId = examiner && examiner.FlightSchoolExaminerId;
    if (!examinerId) {
      this.flightSchoolExaminerId = null;
      this.loading = false;
      return;
    }
    if (examinerId === this.flightSchoolExaminerId) {
      return;
    }

    this.flightSchoolExaminerId = examinerId;
    this.loading = true;
    this.message = null;
    this.errorMessage = null;

    this.subscriptions.push(
      this.qualificationRepository.load(examinerId)
        .subscribe(data => {
          this.buildQuestions(data);
          this.loading = false;
          this.dirty = false;
        }, () => {
          this.loading = false;
          this.errorMessage = 'Qualification questions could not be loaded.';
        })
    );
  }

  private buildQuestions(data: UnitMemberQualificationData): void {
    const responsesByTypeId = new Map<number, UnitMemberQualificationResponseEntity>();
    (data.responses || []).forEach(response =>
      responsesByTypeId.set(response.UnitMemberQualificationTypeId, response)
    );

    const questions = (data.definitions || []).map(definition => {
      const existingResponse = responsesByTypeId.get(definition.UnitMemberQualificationTypeId);
      const response = existingResponse || AppMetaData.UnitMemberQualificationResponse.CreateEntity();

      response.FlightSchoolExaminerId = this.flightSchoolExaminerId;
      response.UnitMemberQualificationTypeId = definition.UnitMemberQualificationTypeId;

      return {
        definition,
        response,
        options: (data.options || []).filter(option =>
          option.UnitMemberQualificationTypeId === definition.UnitMemberQualificationTypeId
        )
      } as UnitMemberQualificationQuestion;
    });

    this.singleChoiceQuestions = questions.filter(question =>
      question.definition.UnitMemberQualificationResponseTypeId === this.responseTypes.singleChoice
    );
    this.tableQuestions = questions.filter(question =>
      question.definition.UnitMemberQualificationResponseTypeId !== this.responseTypes.singleChoice
    );
    this.tableQuestions.forEach((question, index) => question.itemNumber = index + 1);
  }

  private applySavedResponses(savedResponses: UnitMemberQualificationResponseEntity[]): void {
    const savedByTypeId = new Map<number, UnitMemberQualificationResponseEntity>();
    savedResponses.forEach(response => savedByTypeId.set(response.UnitMemberQualificationTypeId, response));

    this.singleChoiceQuestions.concat(this.tableQuestions).forEach(question => {
      const savedResponse = savedByTypeId.get(question.definition.UnitMemberQualificationTypeId);
      if (savedResponse) {
        question.response = savedResponse;
      }
    });
  }

  private hasAnswer(response: UnitMemberQualificationResponseEntity): boolean {
    return response.YesNoValue !== null && response.YesNoValue !== undefined ||
      response.NumericValue !== null && response.NumericValue !== undefined ||
      response.UnitMemberQualificationOptionId !== null && response.UnitMemberQualificationOptionId !== undefined;
  }

  private getFormActionInterceptor(): DwFormActionInterceptor {
    return {
      interceptorName: 'unitMemberQualificationQuestionnaire',
      order: 50,
      onBeforeActionWait: true,
      onBeforeAction: (actionPref: DwActionPreference<DwFormActionConfig>) => {
        if (actionPref.actionKey === DwFormActionKeys.formCancel && this.dirty && this.flightSchoolExaminerId) {
          return this.reloadResponsesForCancel();
        }

        const isSave = actionPref.actionKey === DwFormActionKeys.formSave ||
          actionPref.actionKey === DwFormActionKeys.formWorkflow;

        if (!isSave || !this.dirty || !this.flightSchoolExaminerId) {
          return of({ abort: false });
        }

        return this.saveResponses();
      }
    };
  }

  private reloadResponsesForCancel(): Observable<DwFormActionInterceptResult> {
    return this.qualificationRepository.load(this.flightSchoolExaminerId).pipe(
      map(data => {
        this.buildQuestions(data);
        this.dirty = false;
        this.message = null;
        this.errorMessage = null;
        return { abort: false };
      }),
      catchError(() => of({
        abort: true,
        message: 'Qualification responses could not be restored. Please reload the Unit Member.'
      }))
    );
  }

  private markDirty(): void {
    this.dirty = true;
    this.formApi.formGroup.markAsDirty();
    this.message = null;
    this.errorMessage = null;
  }
}
